/**
 * 液化对话框（ADR-0013）。
 *
 * 全屏模态 + 实时预览：拖拽笔刷时每次 pointermove 只重算脏矩形并贴到
 * `<canvas>` 上（`onPointer` 回调返回的 `LiquifyPreviewFrame`），不是等
 * 全部画完再重画一次——`spikes/liquify-preview/` 已经验证过这个路径的
 * 性能。指针坐标经画布内在分辨率换算成文档像素坐标，实际的会话状态/
 * wasm 重采样全部在装配层（main.tsx）持有的 `LiquifyController` 里，
 * 这里只负责收指针事件、转发、把返回帧画上去——跟其余对话框一样不持有
 * 业务对象，只用回调 props（CLAUDE.md 规则 4）。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type {
  LiquifyBrushOptions,
  LiquifyMode,
  LiquifyPreviewFrame,
} from '../../interaction/tools/liquify-tool';
import styles from './LiquifyDialog.module.css';

const MODE_LABELS: Readonly<Record<LiquifyMode, string>> = {
  push: '推',
  twirlCw: '顺时针扭转',
  twirlCcw: '逆时针扭转',
  pucker: '收缩',
  bloat: '膨胀',
};

/** 每种模式把 0-100 的"强度"滑杆映射到各自公式实际吃的单位（弧度/比例/倍率）。 */
const STRENGTH_SCALE: Readonly<Record<LiquifyMode, number>> = {
  push: 0.03,
  twirlCw: 0.006,
  twirlCcw: 0.006,
  pucker: 0.009,
  bloat: 0.009,
};

export interface LiquifyDialogProps {
  readonly open: boolean;
  /** 会话对应的文档尺寸——预览画布按这个尺寸建立，坐标就是文档像素坐标。 */
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** 取消：外面负责调 `LiquifyController.cancel()`。 */
  readonly onClose: () => void;
  readonly onPointer: (
    phase: 'down' | 'move' | 'up',
    x: number,
    y: number,
    settings: LiquifyBrushOptions,
  ) => Promise<LiquifyPreviewFrame | null>;
  /** 打开时拉一次全图初始预览（未变形，即原始像素）。 */
  readonly onRequestInitialFrame: () => Promise<LiquifyPreviewFrame | null>;
  /** 确定：外面负责调 `LiquifyController.commit()`。 */
  readonly onApply: () => Promise<boolean>;
}

const DEFAULT_RADIUS = 40;
const DEFAULT_STRENGTH = 50;

export function LiquifyDialog(props: LiquifyDialogProps): JSX.Element {
  const [mode, setMode] = createSignal<LiquifyMode>('push');
  const [radius, setRadius] = createSignal(DEFAULT_RADIUS);
  const [strength, setStrength] = createSignal(DEFAULT_STRENGTH);
  const [busy, setBusy] = createSignal(false);
  let canvasEl: HTMLCanvasElement | undefined;
  let dragging = false;
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    if (props.open) {
      setMode('push');
      setRadius(DEFAULT_RADIUS);
      setStrength(DEFAULT_STRENGTH);
      setBusy(false);
      dragging = false;
      void props.onRequestInitialFrame().then((frame) => {
        if (frame !== null) paintFrame(frame);
      });
    }
  });

  function paintFrame(frame: LiquifyPreviewFrame): void {
    const ctx = canvasEl?.getContext('2d');
    if (ctx == null || frame.rect.width <= 0 || frame.rect.height <= 0) return;
    const image = new ImageData(Uint8ClampedArray.from(frame.data), frame.rect.width, frame.rect.height);
    ctx.putImageData(image, frame.rect.x, frame.rect.y);
  }

  function settings(): LiquifyBrushOptions {
    const m = mode();
    return { mode: m, radius: radius(), strength: strength() * STRENGTH_SCALE[m] };
  }

  /** 客户端坐标（CSS 像素）→ 画布内在分辨率的文档像素坐标。 */
  function toDocPoint(event: PointerEvent): { x: number; y: number } {
    const canvas = canvasEl!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  }

  function onPointerDown(event: PointerEvent): void {
    if (busy()) return;
    dragging = true;
    canvasEl?.setPointerCapture(event.pointerId);
    const { x, y } = toDocPoint(event);
    void props.onPointer('down', x, y, settings());
  }

  function onPointerMove(event: PointerEvent): void {
    if (!dragging) return;
    const { x, y } = toDocPoint(event);
    void props.onPointer('move', x, y, settings()).then((frame) => {
      if (frame !== null) paintFrame(frame);
    });
  }

  function onPointerUp(event: PointerEvent): void {
    if (!dragging) return;
    dragging = false;
    canvasEl?.releasePointerCapture(event.pointerId);
    const { x, y } = toDocPoint(event);
    void props.onPointer('up', x, y, settings());
  }

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} data-testid="liquify-backdrop">
        <div class={styles.dialog} role="dialog" ref={(el) => (dialogRef = el)} aria-label="液化" data-testid="liquify-dialog">
          <h2 class={styles.title}>液化</h2>

          <div class={styles.toolbar}>
            <label class={styles.row}>
              <span class={styles.label}>模式</span>
              <select
                class={styles.select}
                value={mode()}
                data-testid="liquify-mode"
                onChange={(e) => setMode(e.currentTarget.value as LiquifyMode)}
              >
                <For each={Object.keys(MODE_LABELS) as LiquifyMode[]}>
                  {(m) => <option value={m}>{MODE_LABELS[m]}</option>}
                </For>
              </select>
            </label>
            <label class={styles.row}>
              <span class={styles.label}>大小</span>
              <input
                type="range"
                min="5"
                max="200"
                value={radius()}
                data-testid="liquify-radius"
                onInput={(e) => setRadius(Number(e.currentTarget.value))}
              />
              <span class={styles.value}>{radius()}</span>
            </label>
            <label class={styles.row}>
              <span class={styles.label}>强度</span>
              <input
                type="range"
                min="1"
                max="100"
                value={strength()}
                data-testid="liquify-strength"
                onInput={(e) => setStrength(Number(e.currentTarget.value))}
              />
              <span class={styles.value}>{strength()}</span>
            </label>
          </div>

          <div class={styles.canvasWrap}>
            <canvas
              ref={(el) => (canvasEl = el)}
              width={props.canvasWidth}
              height={props.canvasHeight}
              class={styles.canvas}
              data-testid="liquify-canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            />
          </div>

          <p class={styles.hint}>
            只对当前活动图层生效；推/扭转/收缩/膨胀是简化版笔刷，不支持冻结区域与重建。
          </p>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="liquify-apply"
              disabled={busy()}
              onClick={() => {
                setBusy(true);
                void props.onApply().finally(() => setBusy(false));
              }}
            >
              {busy() ? '处理中…' : '确定'}
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
